"use client";

import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";

import FormInput from "@/components/ui/FormInput/FormInput";
import LinkButton from "@/components/ui/Link";
import toast from "react-hot-toast";
import Button from "@/components/ui/Button";

import LogoIcon from "../../../../../public/logo.svg";

import { useRequestOtp, useRequestOtpForLogin } from "@/queries/auth";
import { RequestOtpErrorResponse } from "@/types/auth";
import { stashUtmSource } from "@/lib/utmAttribution";

const Login = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const returnToQs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";

  // Persist campaign attribution (e.g. ?utm_source=elecompwebideh1405 from
  // the elecomp1405 QR redirect) so it survives the hop through /verify or
  // /register — those pages don't receive this page's query string.
  useEffect(() => {
    stashUtmSource(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mutateAsync, isPending } = useRequestOtpForLogin();
  const { mutateAsync: mutateAsync2, isPending: isPending2 } = useRequestOtp();

  const isPhoneValid = (phone: string) => /^09\d{9}$/.test(phone);

  const [formData, setFormData] = useState({
    phone: "",
  });

  const toEnglishDigits = (str: string) =>
    str
      .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
      .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

  const handleChange = useCallback(
    (
      e: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      const { name, type } = e.target;
      const rawValue =
        type === "number"
          ? String((e.target as HTMLInputElement).valueAsNumber)
          : e.target.value;

      const finalValue = name === "phone" ? toEnglishDigits(rawValue) : rawValue;

      setFormData((prev) => ({ ...prev, [name]: finalValue }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
     
      if (!isPhoneValid(formData.phone)) {
        toast.error("شماره موبایل نامعتبر است.");
        return;
      }

      try {
        await mutateAsync({
          phone_number: formData.phone,
        });

        localStorage.removeItem("verify_otp_timer");
        localStorage.setItem("phonenumber", formData.phone);

        toast.success("کد ورود ارسال شد");

        router.push(`/verify${returnToQs ? `${returnToQs}&` : "?"}redirect=${encodeURIComponent(returnTo || "/")}`.replace(/&$/, ""));
      } catch (err) {
        
        const apiError = err as RequestOtpErrorResponse;
        if (apiError?.error?.messages?.[0] !== "User not found" && apiError?.error?.messages?.[0] !== "کاربر یافت نشد") {
          toast.error(apiError?.error?.messages?.[0] || "خطا در ارسال کد");
        }
        if (apiError?.error?.messages?.[0] === "User not found" || apiError?.error?.messages?.[0] === "کاربر یافت نشد") {
          try {
            await mutateAsync2({
              phone_number: formData.phone,
            });
            localStorage.removeItem("verify_otp_timer");
            localStorage.setItem("phonenumber", formData.phone);
            toast.success("کد ارسال شد ");

            const utmSource = searchParams.get("utm_source");
            const registerParams = new URLSearchParams();
            if (utmSource) registerParams.set("utm_source", utmSource);
            if (returnTo) registerParams.set("returnTo", returnTo);
            const qs = registerParams.toString();
            router.push(qs ? `/register?${qs}` : "/register");
          } catch (err) {

            const apiError = err as RequestOtpErrorResponse;

            toast.error(apiError?.error?.messages?.[0] || "خطا در ارسال کد");
          }
        }
      }
    },
    [formData.phone, mutateAsync, mutateAsync2, returnTo, returnToQs, router, searchParams],
  );

  return (
    <div className="relative flex items-center justify-center flex-col p-4 md:px-0 h-screen w-screen">
      {/* <Image src={BackLogin} alt="Login Background" fill priority className="object-cover" />

            <div className="absolute inset-0 bg-black/80 backdrop-blur-xs" /> */}

      <form
        className="flex w-full md:w-[480px] max-w-[480px] flex-col items-center rounded-2xl p-8 backdrop-blur-md"
        onSubmit={handleSubmit}
      >
        <header className="relative w-full flex items-center justify-center">
          <LinkButton
            className="flex items-center top-14 right-14 z-50"
            href="/"
            variant="default"
          >
            <Image src={LogoIcon} width={38} height={38} alt="ایده نما" />
            <h1 className="text-2xl ms-3">ایده نما</h1>
          </LinkButton>
        </header>

        <p className="text-md mb-4">شماره تلفن همراه خود را وارد نمایید.</p>

        <div className="w-full max-w-[340px] mb-4">
          <FormInput
            name="phone"
            type="tel"
            className={`mb-1 text-base ${!formData.phone ? "text-right" : ""}`}
            placeholder="شماره تلفن همراه"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>
        <div className="w-full max-w-[340px] mb-4">
          <Button
            type="submit"
            disabled={!formData.phone || isPending}
            className="w-full mt-4"
          >
            {isPending ? "در حال ارسال..." : "ورود"}
          </Button>
        </div>

        {/* <LinkButton variant="default" className="mt-5" href="/loginpassword">
                    ورود با رمز ثابت
                </LinkButton> */}
      </form>
    </div>
  );
};

export default Login;
